if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        this.allMediaItems = [...this.querySelectorAll('.product__media-item')];
        this.allThumbnailItems = [...this.querySelectorAll('.product__thumbs-item')];
        this.allMediaItems.forEach((item, index) => {
          item.dataset.sourceIndex = item.dataset.mediaIndex || String(index);
        });
        this.activeGalleryColor = '';
        this.applyGalleryColor(this.dataset.primaryGalleryColor || '', false);
        this.init();
      }

      normalizeGalleryColor(value) {
        return String(value || '')
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '');
      }

      galleryRoleRank(item) {
        switch (item?.dataset?.galleryRole) {
          case 'hero': return 0;
          case 'lifestyle': return 1;
          case 'detail': return 2;
          case 'size_chart': return 3;
          default: return 1;
        }
      }

      applyGalleryColor(color, refresh = true) {
        const normalizedColor = this.normalizeGalleryColor(color);
        if (!normalizedColor || (refresh && normalizedColor === this.activeGalleryColor)) return;

        const mediaList = this.querySelector('[id^="Slider-Gallery"]');
        const thumbnailList = this.querySelector('[id^="Slider-Thumbnails"]');
        if (!mediaList || !this.allMediaItems?.length) return;

        if (refresh) this.destroySlider();

        const colorOf = (item) => this.normalizeGalleryColor(item.dataset.galleryColor);
        const roleOf = (item) => item.dataset.galleryRole || 'shared';
        const primaryColor = this.normalizeGalleryColor(this.dataset.primaryGalleryColor);
        const sourceOrder = (items) => [...items].sort((left, right) =>
          Number(left.dataset.sourceIndex || 0) - Number(right.dataset.sourceIndex || 0),
        );
        const byColorAndRole = (targetColor, role) => sourceOrder(this.allMediaItems.filter((item) =>
          colorOf(item) === targetColor && roleOf(item) === role,
        ));
        const sharedByRole = (role) => sourceOrder(this.allMediaItems.filter((item) =>
          !colorOf(item) && roleOf(item) === role,
        ));
        const selectedHero = byColorAndRole(normalizedColor, 'hero');
        const primaryHero = byColorAndRole(primaryColor, 'hero');
        const selectedLifestyle = byColorAndRole(normalizedColor, 'lifestyle');
        const primaryLifestyle = byColorAndRole(primaryColor, 'lifestyle');
        const hasFullSelectedGallery = selectedHero.length > 0 && selectedLifestyle.length > 0;
        const contentColor = hasFullSelectedGallery ? normalizedColor : primaryColor;
        const contentLifestyle = hasFullSelectedGallery ? selectedLifestyle : primaryLifestyle;
        const contentDetail = byColorAndRole(contentColor, 'detail');
        const contentSizeChart = byColorAndRole(contentColor, 'size_chart');
        const selected = [
          ...(selectedHero.length ? selectedHero : primaryHero),
          ...contentLifestyle,
          ...(contentDetail.length ? contentDetail : sharedByRole('detail')),
          ...(contentSizeChart.length ? contentSizeChart : sharedByRole('size_chart')),
        ];

        // Không làm rỗng gallery nếu dữ liệu màu của listing cũ không khớp quy ước mới.
        if (!selected.length) return;

        const hero = selected.find((item) => roleOf(item) === 'hero') || selected[0];
        this.allMediaItems.forEach((item) => item.removeAttribute('data-variant-hero'));
        if (hero) hero.setAttribute('data-variant-hero', '');

        mediaList.replaceChildren(...selected);
        selected.forEach((item, index) => {
          item.dataset.mediaIndex = String(index);
          item.querySelectorAll('.js-photoswipe--zoom').forEach((button) => {
            button.dataset.mediaIndex = String(index);
          });
        });

        if (thumbnailList && this.allThumbnailItems?.length) {
          const selectedIds = new Set(selected.map((item) => item.dataset.mediaId));
          const thumbnails = this.allThumbnailItems
            .filter((item) => selectedIds.has(item.dataset.target))
            .sort((left, right) => {
              const leftMedia = selected.findIndex((item) => item.dataset.mediaId === left.dataset.target);
              const rightMedia = selected.findIndex((item) => item.dataset.mediaId === right.dataset.target);
              return leftMedia - rightMedia;
            });
          thumbnailList.replaceChildren(...thumbnails);
        }

        this.activeGalleryColor = normalizedColor;
        this.querySelectorAll('[data-selected-color-caption]').forEach((caption) => {
          caption.textContent = color;
        });

        if (this.lightbox?.options) {
          this.lightbox.options.dataSource = this.buildPhotoSwipeDataSource(selected);
        }

        if (refresh) {
          this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
          this.setSliderOptions();
          this.updateMediaLayout();
        }
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'bullets',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: 'auto',
            });
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy(true, true);
          this.sliderInstance = false;
        }
        if (typeof this.thumbsInstance === 'object') {
          this.thumbsInstance.slider.destroy(true, true);
          this.thumbsInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        const dataSource = this.buildPhotoSwipeDataSource(allMedia);

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { currIndex } = this.lightbox.pswp;
            if (this.sliderInstance.slider.realIndex !== currIndex) {
              this.sliderInstance.slider.slideToLoop(currIndex, 100, false);
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      buildPhotoSwipeDataSource(allMedia) {
        const dataSource = [];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }
        return dataSource;
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        const selectedMedia = this.allMediaItems?.find(
          (item) => String(item.dataset.mediaId) === String(variant.featured_media.id)
        );
        const nextColor = selectedMedia?.dataset?.galleryColor || '';
        if (nextColor) this.applyGalleryColor(nextColor);
        this.setVariantHero(variant);
      }

      setVariantHero(variant) {
        const hero = this.querySelector('[data-variant-hero]');
        const image = hero?.querySelector('img');
        const source = variant.featured_media?.preview_image?.src || variant.featured_media?.src;
        if (!hero || !image || !source) return;

        image.src = source;
        image.srcset = '';
        image.alt = variant.featured_media.alt || image.alt;
        hero.dataset.src = source;
        hero.dataset.variantMediaId = variant.featured_media.id;
        hero.dataset.pswpWidth = variant.featured_media.preview_image?.width || hero.dataset.pswpWidth;
        hero.dataset.pswpHeight = variant.featured_media.preview_image?.height || hero.dataset.pswpHeight;

        if (this.sliderInstance?.slider) this.sliderInstance.slider.slideToLoop(0);
      }

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile) {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
